/* Anherb — cart/menu drawer close guard.
 *
 * WHY THIS EXISTS
 * The theme's MenuDrawer.closeAnimation (assets/global.js) uses requestAnimationFrame
 * as a 400ms timer:
 *
 *   closeAnimation(detailsElement) {
 *     const handleAnimation = time => {
 *       if (time - animationStart < 400) requestAnimationFrame(handleAnimation);
 *       else { body.classList.remove(closing); el.classList.remove('menu-closing');
 *              el.removeAttribute('open'); }
 *     };
 *     requestAnimationFrame(handleAnimation);
 *   }
 *
 * Browsers freeze requestAnimationFrame in a backgrounded tab. If the customer leaves
 * the page mid-close — switches apps on a phone, answers a notification, changes tab —
 * that chain never reaches the else branch. The <details> keeps its `open` attribute,
 * `menu-closing` is never removed, and the full-viewport scrim
 * (summary.header__icon::after, position:fixed, z-index:30) stays hit-testable.
 * Every subsequent click anywhere on the page is then swallowed by the SUMMARY element.
 *
 * Confirmed 10 Aug 2026: with the tab hidden, rAF ticks = 0/sec and a 20-point grid
 * hit-tests 20/20 to SUMMARY.header__icon. With the tab focused it closes correctly
 * every time. So this is not a live bug — it is a latent one that a real customer can
 * hit by backgrounding their browser during the 400ms close.
 *
 * WHAT THIS DOES
 * Two independent fallbacks that force-complete a stalled close. Neither touches the
 * normal path: the legitimate OPEN state is `menu-opening` + [open], which is left
 * alone. Only `menu-closing` persisting past the grace window is treated as stuck.
 */
(function () {
  'use strict';

  var GRACE_MS = 1200;      // theme animates for 400ms; leave generous headroom
  var REVISIT_MS = 600;     // after returning to the page, let rAF finish on its own first
  var SELECTOR = '.cart-drawer-container, .menu-drawer-container';

  var timer = null;

  function drawers() {
    return Array.prototype.slice.call(document.querySelectorAll(SELECTOR));
  }

  function isStuck(el) {
    return el.classList.contains('menu-closing');
  }

  function forceComplete(el) {
    el.classList.remove('menu-closing');
    el.classList.remove('menu-opening');
    el.removeAttribute('open');

    var summary = el.querySelector('summary');
    if (summary) summary.setAttribute('aria-expanded', 'false');

    // Strip the drawer's body-state classes without disturbing template-level ones
    // (template-product, loaded, ...). Matches mini-cart--open / --opening / --closing
    // and the menu drawer's equivalents.
    Array.prototype.slice.call(document.body.classList).forEach(function (cls) {
      if (/--(open|opening|closing)$/.test(cls)) document.body.classList.remove(cls);
    });

    document.dispatchEvent(
      new CustomEvent('anh:drawer-close-recovered', { detail: { drawer: el.className } })
    );
  }

  function sweep() {
    drawers().forEach(function (el) {
      if (isStuck(el)) forceComplete(el);
    });
  }

  function scheduleSweep(delay) {
    clearTimeout(timer);
    timer = setTimeout(sweep, delay);
  }

  // 1. Watch for a close starting. If `menu-closing` is still there after the grace
  //    window, rAF did not finish the job.
  function watch(el) {
    new MutationObserver(function () {
      if (isStuck(el)) scheduleSweep(GRACE_MS);
    }).observe(el, { attributes: true, attributeFilter: ['class'] });
  }

  // 2. Coming back to the page is the moment the damage would otherwise be visible.
  //    Give rAF a beat to resume and finish, then clean up whatever it left behind.
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') scheduleSweep(REVISIT_MS);
  });
  window.addEventListener('pageshow', function () { scheduleSweep(REVISIT_MS); });
  window.addEventListener('focus', function () { scheduleSweep(REVISIT_MS); });

  function init() {
    drawers().forEach(watch);
    sweep(); // in case we loaded into an already-stuck state (bfcache restore)
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
